import React from "react";
import {
  Grid,
  Stack,
  Box,
  Switch,
  FormControlLabel,
  Typography,
  Divider,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";

import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

import { updatePaymentIntegration } from "../redux/actions/settingsActions";

const schema = Yup.object({
  active: Yup.boolean(),
  gatewayApiKey: Yup.string().nullable(),
  gatewayWebhookSecret: Yup.string().nullable(),
  allowsMpesaStk: Yup.boolean(),
  allowsMpesaC2b: Yup.boolean(),
  allowsBankIpn: Yup.boolean(),
});

const UpdatePaymentIntegrationForm = ({ config, loading }) => {
  const dispatch = useDispatch();

  const isConfigured = config?.hasGatewayApiKey || false;

  const initialValues = {
    active: config?.active || false,
    gatewayApiKey: "",
    gatewayWebhookSecret: "",
    allowsMpesaStk: config?.allowsMpesaStk || false,
    allowsMpesaC2b: config?.allowsMpesaC2b || false,
    allowsBankIpn: config?.allowsBankIpn || false,
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={(values) => {
        const payload = { ...values };
        if (!payload.gatewayApiKey) payload.gatewayApiKey = null;
        if (!payload.gatewayWebhookSecret) payload.gatewayWebhookSecret = null;
        dispatch(updatePaymentIntegration(payload));
      }}
    >
      {({ values, setFieldValue, dirty, isValid }) => (
        <Form>
          <Stack spacing={3}>
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    {isConfigured ? (
                      <>
                        <CheckCircleIcon color="success" fontSize="small" />
                        <Typography variant="body2" color="success.main">
                          API Keys Configured
                        </Typography>
                      </>
                    ) : (
                      <>
                        <ErrorIcon color="error" fontSize="small" />
                        <Typography variant="body2" color="error">
                          API Keys Missing
                        </Typography>
                      </>
                    )}
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.active}
                        onChange={(e) =>
                          setFieldValue("active", e.target.checked)
                        }
                        color="secondary"
                      />
                    }
                    label="Enable Payment Gateway"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormTextField
                    name="gatewayApiKey"
                    label="Gateway API Key"
                    type="password"
                    placeholder={
                      isConfigured
                        ? "Enter new key to update..."
                        : "pk_test_..."
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormTextField
                    name="gatewayWebhookSecret"
                    label="Webhook Secret"
                    type="password"
                    placeholder={
                      isConfigured
                        ? "Enter new secret to update..."
                        : "whsec_..."
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }}>
                    <Typography variant="caption">
                      SUPPORTED CHANNELS
                    </Typography>
                  </Divider>
                  <Stack direction="row" spacing={3}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.allowsMpesaStk}
                          onChange={(e) =>
                            setFieldValue("allowsMpesaStk", e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="M-Pesa STK Push"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.allowsMpesaC2b}
                          onChange={(e) =>
                            setFieldValue("allowsMpesaC2b", e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="M-Pesa C2B / Paybill"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.allowsBankIpn}
                          onChange={(e) =>
                            setFieldValue("allowsBankIpn", e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="Bank IPN"
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Box>
            <SubmitButton
              loading={loading}
              title="Save Gateway Settings"
              disabled={!dirty || !isValid}
              startIcon={<SaveOutlinedIcon />}
            />
          </Stack>
        </Form>
      )}
    </Formik>
  );
};
export default UpdatePaymentIntegrationForm;
